import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hook';
import CloneExternalLinks from '@/features/bot/CloneExternalLinks';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { selectCurrentDataState } from '../../create.slice';

const AddDescription = () => {
  const { t } = useTranslation();
  const { getValues, control, reset, setValue, formState } = useFormContext();

  const currentData = useAppSelector(selectCurrentDataState);

  const handleUpdateExternalLinks = (
    externalLinks: Array<{
      logo: string;
      link: string;
    }>
  ) => {
    setValue('cloneExtra', {
      ...(getValues('cloneExtra') || {}),
      externalLinks: externalLinks,
    });
  };

  useEffect(() => {
    if (currentData.cloneExtra) {
      reset({
        cloneExtra: {
          ...(currentData.cloneExtra || {}),
          data: {
            name: currentData.cloneExtra.data?.name || '',
            profession: currentData.cloneExtra.data?.profession || '',
            bio: currentData.cloneExtra.data?.bio || '',
          },
          externalLinks: JSON.parse(JSON.stringify(currentData.cloneExtra.externalLinks ?? [])),
        },
      });
    }
  }, [currentData]);

  if (!getValues('cloneExtra')?.data) {
    return <div>Loading...</div>;
  }
  return (
    <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-inline-flex md:tw-w-2/3 tw-w-full">
      <div className="tw-flex tw-flex-col tw-gap-y-6">
        <div className="description-infos">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Wizard/CloneExtraDescription/key1')}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('Wizard/CloneExtraDescription/key2')}
              </div>
            </div>
          </div>

          <Form.Group className="tw-mb-3">
            <Form.Label>{t('Wizard/CloneExtraDescription/key3')}</Form.Label>
            <Controller
              name="cloneExtra.data.name"
              control={control}
              render={({ field }) => (
                <Form.Control {...field} placeholder={t('Wizard/CloneExtraDescription/key4')} isInvalid={!!formState.errors?.cloneExtra?.data?.name} />
              )}
            />
            <Form.Control.Feedback type="invalid">{formState.errors?.cloneExtra?.data?.name?.message as string}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>{t('Wizard/CloneExtraDescription/key5')}</Form.Label>
            <Controller
              name="cloneExtra.data.profession"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  placeholder={t('Wizard/CloneExtraDescription/key6').toString()}
                  isInvalid={!!formState.errors?.cloneExtra?.data?.profession}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">{formState.errors?.cloneExtra?.data?.profession?.message as string}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>{t('Wizard/CloneExtraDescription/key7')}</Form.Label>
            <Controller
              name="cloneExtra.data.bio"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  as={'textarea'}
                  rows={4}
                  placeholder={t('Wizard/CloneExtraDescription/key8').toString()}
                  isInvalid={!!formState.errors?.cloneExtra?.data?.bio}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">{formState.errors?.cloneExtra?.data?.bio?.message as string}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="external-links">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t('Wizard/CloneExtraDescription/key9')}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('Wizard/CloneExtraDescription/key10')}
              </div>
            </div>
          </div>

          <CloneExternalLinks initialData={currentData.cloneExtra?.externalLinks ?? []} onChange={handleUpdateExternalLinks} />

          {!!formState.errors?.cloneExtra?.externalLinks && (
            <span className="tw-text-sm tw-text-red-600 tw-mt-2 tw-block">{formState.errors?.cloneExtra?.externalLinks?.message as string}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(AddDescription);
